// =============================================================================
// NOTIFICATIONS & MESSAGING SYSTEM
// Handles Finance-Sales communication, alerts, and audit trail
// =============================================================================

import { logger } from '@/utils/logger'

const log = logger.module('Notifications')

// -----------------------------------------------------------------------------
// DATA KEYS
// -----------------------------------------------------------------------------
const MESSAGES_KEY = 'blissFinanceMessages'
const NOTIFICATIONS_KEY = 'blissNotifications'

// =============================================================================
// TYPES
// =============================================================================

export interface MessageParticipant {
  name: string
  role: string
}

export interface Message {
  messageId: string
  from: MessageParticipant
  to: MessageParticipant
  message: string
  timestamp: string
  read: boolean
}

export interface MessageThread {
  id: string
  requestId: string
  orderId: string | null
  thread: Message[]
  status: 'active' | 'pending_response' | 'resolved'
  createdAt: string
  updatedAt: string
}

export interface Notification {
  id: string
  recipientId: string
  recipientName: string
  recipientRole: string
  type: string
  title: string
  message: string
  clientName?: string
  requestId?: string
  orderId: string | null
  linkTo: string | null
  read: boolean
  createdAt: string
}

export interface NotificationData {
  message: string
  clientName?: string
  requestId?: string
  orderId?: string | null
  linkTo?: string | null
}

export interface CommunicationSummary {
  thread: MessageThread | null
  notifications: Notification[]
  messageCount: number
  notificationCount: number
  lastActivity: string | null
  hasPendingResponse: boolean
}

export interface SMSLogEntry {
  id: string
  phone: string
  message: string
  status: string
  timestamp: string
}

// -----------------------------------------------------------------------------
// MESSAGE THREAD FUNCTIONS
// -----------------------------------------------------------------------------

/**
 * Get all message threads
 */
// In-memory cache for message threads (session-only)
let messageThreadsCache: MessageThread[] = []

export const getAllMessageThreads = (): MessageThread[] => {
  return messageThreadsCache
}

/**
 * Get message thread for a specific request/order
 */
export const getMessageThread = (requestId: string): MessageThread | null => {
  const threads = getAllMessageThreads()
  return threads.find(t => t.requestId === requestId) || null
}

/**
 * Create a new message thread
 */
export const createMessageThread = (requestId: string, orderId: string | null = null): MessageThread => {
  const threads = getAllMessageThreads()

  // Check if thread already exists
  const existing = threads.find(t => t.requestId === requestId)
  if (existing) return existing

  const newThread: MessageThread = {
    id: `FM-${Date.now()}`,
    requestId,
    orderId,
    thread: [],
    status: 'active',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  }

  threads.push(newThread)
  messageThreadsCache = threads

  return newThread
}

/**
 * Add a message to a thread
 */
export const addMessageToThread = (requestId: string, message: Omit<Message, 'messageId' | 'timestamp' | 'read'>): Message => {
  const threads = getAllMessageThreads()
  let threadIndex = threads.findIndex(t => t.requestId === requestId)

  // Create thread if doesn't exist
  if (threadIndex === -1) {
    const newThread = createMessageThread(requestId)
    threads.push(newThread)
    threadIndex = threads.length - 1
  }

  const newMessage: Message = {
    messageId: `MSG-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
    from: message.from,
    to: message.to,
    message: message.message,
    timestamp: new Date().toISOString(),
    read: false
  }

  threads[threadIndex].thread.push(newMessage)
  threads[threadIndex].updatedAt = new Date().toISOString()
  threads[threadIndex].status = message.from.role === 'finance' ? 'pending_response' : 'resolved'

  messageThreadsCache = threads

  return newMessage
}

/**
 * Mark messages as read
 */
export const markThreadMessagesRead = (requestId: string, readerRole: string): boolean => {
  const threads = getAllMessageThreads()
  const threadIndex = threads.findIndex(t => t.requestId === requestId)

  if (threadIndex === -1) return false

  threads[threadIndex].thread.forEach(msg => {
    // Mark as read if the reader is the recipient
    if (msg.to.role === readerRole && !msg.read) {
      msg.read = true
    }
  })

  threads[threadIndex].updatedAt = new Date().toISOString()
  messageThreadsCache = threads

  return true
}

/**
 * Get unread message count for a specific request
 */
export const getUnreadMessageCount = (requestId: string, readerRole: string): number => {
  const thread = getMessageThread(requestId)
  if (!thread) return 0

  return thread.thread.filter(msg =>
    msg.to.role === readerRole && !msg.read
  ).length
}

/**
 * Check if thread has pending response from sales
 */
export const hasPendingResponse = (requestId: string): boolean => {
  const thread = getMessageThread(requestId)
  return thread?.status === 'pending_response'
}

// -----------------------------------------------------------------------------
// NOTIFICATION FUNCTIONS
// -----------------------------------------------------------------------------

/**
 * Get all notifications
 */
// In-memory cache for notifications (session-only)
let notificationsCache: Notification[] = []

export const getAllNotifications = (): Notification[] => {
  return notificationsCache
}

/**
 * Get notifications for a specific user
 */
export const getNotificationsForUser = (userId: string): Notification[] => {
  const notifications = getAllNotifications()
  return notifications.filter(n => n.recipientId === userId)
}

/**
 * Get unread notification count for a user
 */
export const getUnreadNotificationCount = (userId: string): number => {
  const notifications = getNotificationsForUser(userId)
  return notifications.filter(n => !n.read).length
}

/**
 * Create a notification
 */
export const createNotification = (
  recipient: { id: string; name: string; role: string },
  type: string,
  data: NotificationData
): Notification => {
  const notifications = getAllNotifications()

  const titles: Record<string, string> = {
    finance_question: 'Question from Finance',
    finance_approved: 'Invoice Approved',
    finance_rejected: 'Invoice Rejected - Action Required',
    sales_response: 'Response from Sales',
    production_release: '🔔 New Work Order Available',
    order_claimed: 'Work Order Claimed',
    fabrication_update: 'Fabrication Update'
  }

  const newNotification: Notification = {
    id: `NOTIF-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
    recipientId: recipient.id,
    recipientName: recipient.name,
    recipientRole: recipient.role,
    type,
    title: titles[type] || 'Notification',
    message: data.message,
    clientName: data.clientName,
    requestId: data.requestId,
    orderId: data.orderId || null,
    linkTo: data.linkTo || null,
    read: false,
    createdAt: new Date().toISOString()
  }

  notifications.unshift(newNotification) // Add to front

  // Keep only last 100 notifications per user
  const userNotifications = notifications.filter(n => n.recipientId === recipient.id)
  if (userNotifications.length > 100) {
    const idsToKeep = new Set(userNotifications.slice(0, 100).map(n => n.id))
    const filtered = notifications.filter(n =>
      n.recipientId !== recipient.id || idsToKeep.has(n.id)
    )
    notificationsCache = filtered
  } else {
    notificationsCache = notifications
  }

  return newNotification
}

/**
 * Mark notification as read
 */
export const markNotificationRead = (notificationId: string): boolean => {
  const notifications = getAllNotifications()
  const index = notifications.findIndex(n => n.id === notificationId)

  if (index === -1) return false

  notifications[index].read = true
  notificationsCache = notifications

  return true
}

/**
 * Mark all notifications as read for a user
 */
export const markAllNotificationsRead = (userId: string): boolean => {
  const notifications = getAllNotifications()

  notifications.forEach(n => {
    if (n.recipientId === userId) {
      n.read = true
    }
  })

  notificationsCache = notifications
  return true
}

/**
 * Delete a notification
 */
export const deleteNotification = (notificationId: string): boolean => {
  const notifications = getAllNotifications()
  const filtered = notifications.filter(n => n.id !== notificationId)
  notificationsCache = filtered
  return true
}

// -----------------------------------------------------------------------------
// PARTNER NOTIFICATION FUNCTIONS
// -----------------------------------------------------------------------------

/**
 * Notify all fabrication partners about a new work order
 */
export const notifyPartnersOfNewOrder = (order: any): number => {
  const orderId = order.orderId || order.id
  const clientName = order.client?.name || 'Client'
  const config = order.config || order.lockedSpecs?.config || 'Custom Bunk'

  // Partners are in Supabase - notifications go to all_partners by default
  // Real partner list is fetched via PartnersService when needed
  const partners = [] // Supabase is source of truth

  // Create notifications for "all_partners" recipient
  if (partners.length === 0) {
    createNotification(
      { id: 'all_partners', name: 'Fabrication Partners', role: 'partner' },
      'production_release',
      {
        message: `New work order ready for fabrication: ${config} for ${clientName}`,
        clientName,
        requestId: orderId,
        orderId: orderId,
        linkTo: '/operations/fabrication'
      }
    )
    log.info('Partner notification created for new work order', { orderId })
    return 1
  }

  // Notify each registered partner
  let notificationCount = 0
  partners.forEach((partner: any) => {
    if (partner.status === 'active') {
      createNotification(
        { id: partner.id, name: partner.name, role: 'partner' },
        'production_release',
        {
          message: `New work order ready for fabrication: ${config} for ${clientName}`,
          clientName,
          requestId: orderId,
          orderId: orderId,
          linkTo: '/operations/fabrication'
        }
      )
      notificationCount++
    }
  })

  log.info('Partner notifications created', { count: notificationCount, orderId })
  return notificationCount
}

/**
 * Get partner notifications (unread work orders)
 */
export const getPartnerNotifications = (partnerId = 'all_partners'): Notification[] => {
  const notifications = getAllNotifications()
  return notifications.filter(n =>
    (n.recipientId === partnerId || n.recipientId === 'all_partners') &&
    n.type === 'production_release'
  )
}

/**
 * Get unread partner notification count
 */
export const getUnreadPartnerNotificationCount = (partnerId = 'all_partners'): number => {
  const notifications = getPartnerNotifications(partnerId)
  return notifications.filter(n => !n.read).length
}

// -----------------------------------------------------------------------------
// SMS INTEGRATION STUB (Future Implementation)
// -----------------------------------------------------------------------------

/**
 * Send SMS notification - STUB for future Twilio/MessageBird integration
 */
export const sendSMSNotification = async (phone: string, message: string): Promise<{
  queued: boolean
  provider: string
  message: string
}> => {
  // Future: Integrate with Twilio/MessageBird
  log.info('SMS stub called', { phone, message })

  // TODO: Migrate SMS log to Supabase 'sms_logs' table
  // For now, just log to console (pending Twilio integration)
  console.log('[SMS] Queued for future delivery:', { phone, messageLength: message.length })

  return {
    queued: true,
    provider: 'pending_integration',
    message: 'SMS integration pending - message logged for future delivery'
  }
}

// -----------------------------------------------------------------------------
// CHANGE ORDER NOTIFICATIONS (FULL SUITE)
// -----------------------------------------------------------------------------

export interface ChangeOrderNotificationData {
  orderId: string
  changeOrderId: string
  clientName: string
  reason: string
  costDelta: number
  payoutDelta: number
  rushOrder: boolean
  partnerName?: string
  partnerPhone?: string
  partnerEmail?: string
}

/**
 * Send full suite of change order notifications to partner
 * - In-app banner (immediate)
 * - SMS alert (immediate)
 * - Email with details (immediate)
 */
export async function notifyPartnerOfChangeOrder(
  partnerId: string,
  partnerData: { name: string; phone?: string; email?: string },
  changeOrderData: ChangeOrderNotificationData
): Promise<{
  inApp: boolean
  sms: { queued: boolean; provider: string }
  email: { sent: boolean; error?: string }
}> {
  const { orderId, changeOrderId, clientName, reason, payoutDelta, rushOrder } = changeOrderData
  
  const results = {
    inApp: false,
    sms: { queued: false, provider: 'none' },
    email: { sent: false, error: '' }
  }
  
  // 1. IN-APP NOTIFICATION (Banner alert in Partner Portal)
  try {
    createNotification(
      { id: partnerId, name: partnerData.name, role: 'partner' },
      'change_order_received',
      {
        message: rushOrder 
          ? `🚨 RUSH CHANGE ORDER: ${changeOrderId} for ${clientName}. Check portal ASAP!`
          : `📝 CHANGE ORDER: ${changeOrderId} received for ${clientName}. Additional payout: +$${payoutDelta}`,
        clientName,
        requestId: orderId,
        orderId,
        changeOrderId,
        payoutDelta,
        rushOrder,
        reason,
        linkTo: '/millworks'
      }
    )
    results.inApp = true
    log.info('In-app change order notification created', { partnerId, changeOrderId })
  } catch (err) {
    log.error('Failed to create in-app notification', { err })
  }
  
  // 2. SMS ALERT
  if (partnerData.phone) {
    try {
      const smsMessage = rushOrder
        ? `🚨 BLISS RUSH: Change Order ${changeOrderId} for Job ${orderId}. Check portal ASAP! +$${payoutDelta} payout.`
        : `Bliss: Change Order ${changeOrderId} for Job ${orderId}. +$${payoutDelta} payout. Check portal.`
      
      const smsResult = await sendSMSNotification(partnerData.phone, smsMessage)
      results.sms = smsResult
      log.info('SMS change order notification sent', { phone: partnerData.phone, changeOrderId })
    } catch (err) {
      log.error('Failed to send SMS notification', { err })
    }
  }
  
  // 3. EMAIL with details
  if (partnerData.email) {
    try {
      // TODO: Migrate email log to Supabase 'email_logs' table
      // For now, just log to console (pending full EmailJS integration)
      const subject = rushOrder 
        ? `🚨 RUSH: Change Order ${changeOrderId} - Action Required`
        : `Change Order ${changeOrderId} - New Work Added`
      console.log('[Email] Queued for partner:', { to: partnerData.email, subject })
      
      results.email = { sent: true }
      log.info('Email change order notification queued', { email: partnerData.email, changeOrderId })
    } catch (err: any) {
      log.error('Failed to send email notification', { err })
      results.email = { sent: false, error: err.message }
    }
  }
  
  return results
}

/**
 * Get change order notifications for a partner (for in-app banner display)
 */
export function getChangeOrderNotifications(partnerId: string = 'all_partners'): Array<any> {
  const allNotifications = getAllNotifications()
  
  return allNotifications.filter(n => 
    n.type === 'change_order_received' && 
    (n.recipient?.id === partnerId || partnerId === 'all_partners')
  ).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
}

/**
 * Get unread change order count for partner banner
 */
export function getUnreadChangeOrderCount(partnerId: string = 'all_partners'): number {
  const notifications = getChangeOrderNotifications(partnerId)
  return notifications.filter(n => !n.read).length
}

// -----------------------------------------------------------------------------
// AUDIT / ADMIN FUNCTIONS
// -----------------------------------------------------------------------------

/**
 * Get full communication audit log (for admin review)
 */
export const getCommunicationAuditLog = (): Array<any> => {
  const threads = getAllMessageThreads()
  const notifications = getAllNotifications()

  // Flatten all messages with context
  const allMessages: any[] = []

  threads.forEach(thread => {
    thread.thread.forEach(msg => {
      allMessages.push({
        type: 'message',
        threadId: thread.id,
        requestId: thread.requestId,
        orderId: thread.orderId,
        ...msg
      })
    })
  })

  // Combine with notifications
  const combined = [
    ...allMessages.map(m => ({ ...m, sortDate: m.timestamp })),
    ...notifications.map(n => ({
      type: 'notification',
      ...n,
      sortDate: n.createdAt
    }))
  ]

  // Sort by date descending
  return combined.sort((a, b) =>
    new Date(b.sortDate).getTime() - new Date(a.sortDate).getTime()
  )
}

/**
 * Get communication summary for a specific request
 */
export const getRequestCommunicationSummary = (requestId: string): CommunicationSummary => {
  const thread = getMessageThread(requestId)
  const notifications = getAllNotifications().filter(n => n.requestId === requestId)

  return {
    thread,
    notifications,
    messageCount: thread?.thread.length || 0,
    notificationCount: notifications.length,
    lastActivity: thread?.updatedAt || null,
    hasPendingResponse: thread?.status === 'pending_response'
  }
}
